/**
 * Leaderboard modal: Career / Best Game / Rates tabs list every metric with its leader; tap one
 * for its top ten and where you stand. My Stats is your own stat sheet: every metric, your value
 * and your rank. Filters (Deck, Card Selection, Play Style) narrow every tab to the games that
 * match. Loaded fresh each time it opens or a filter changes (`GET /api/leaderboard`).
 */
import { useEffect, useState } from 'react';
import {
  CARD_SELECTIONS,
  DECKS,
  METRICS,
  METRIC_KINDS,
  PLAY_STYLES,
  t,
  tn,
  type Leaderboard as Board,
  type LeaderboardFilter,
  type MetricBoard,
  type MetricDef,
  type MetricKind,
  type PlayerId,
} from '@kadice/shared';
import { api } from '../api';

type Tab = MetricKind | 'mine';
const TABS: readonly Tab[] = [...METRIC_KINDS, 'mine'];
const DEFS = new Map(METRICS.map((m) => [m.id, m]));

/** A metric's value as shown: counts, percentages, averages, game lengths. */
export function formatMetric(id: string, value: number): string {
  const def = DEFS.get(id);
  switch (def?.format) {
    case 'pct':
      return t('stats.format.pct', { n: Math.round(value * 100) });
    case 'decimal':
      return value.toFixed(2);
    case 'duration': {
      const s = Math.round(value / 1000);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      if (h > 0) return t('stats.format.hm', { h, m });
      if (m > 0) return t('stats.format.ms', { m, s: String(s % 60).padStart(2, '0') });
      return t('stats.format.s', { s });
    }
    default:
      return value.toLocaleString();
  }
}

export const metricName = (id: string) => t(`stats.metric.${id}`);

export function Leaderboard({
  me,
  initialTab = 'total',
  onClose,
}: {
  me: PlayerId | undefined;
  initialTab?: Tab;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<Tab>(initialTab);
  const [filter, setFilter] = useState<LeaderboardFilter>({});
  const [board, setBoard] = useState<Board | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    api.leaderboard(filter).then(
      (b) => live && setBoard(b),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, [filter]);
  // A new filter: back to Loading until its board comes.
  const narrow = (f: LeaderboardFilter) => {
    setBoard(null);
    setFailed(false);
    setFilter(f);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal leaderboard"
        role="dialog"
        aria-modal="true"
        aria-label={t('stats.title')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="row">
          <h3>{t('stats.title')}</h3>
          <span className="spacer" />
          <button onClick={onClose}>{t('common.close')}</button>
        </div>
        <div className="stats-tabs" role="tablist">
          {TABS.map((id) => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              className={tab === id ? 'primary' : undefined}
              onClick={() => setTab(id)}
            >
              {t(`stats.tab.${id}`)}
            </button>
          ))}
        </div>
        <div className="stats-filters">
          <FilterSelect
            label={t('stats.filter.deck')}
            value={filter.deck}
            options={DECKS.map((d) => [d, t(`lobby.deck.${d}`)])}
            onChange={(deck) => narrow({ ...filter, deck })}
          />
          <FilterSelect
            label={t('stats.filter.cardSelection')}
            value={filter.cardSelection}
            options={CARD_SELECTIONS.map((c) => [c, t(`lobby.cardSelection.${c}`)])}
            onChange={(cardSelection) => narrow({ ...filter, cardSelection })}
          />
          <FilterSelect
            label={t('stats.filter.playStyle')}
            value={filter.playStyle}
            options={PLAY_STYLES.map((p) => [p, t(`options.${p}`)])}
            onChange={(playStyle) => narrow({ ...filter, playStyle })}
          />
        </div>
        {failed ? (
          <p className="muted">{t('stats.failed')}</p>
        ) : !board ? (
          <p className="muted">{t('stats.loading')}</p>
        ) : tab === 'mine' ? (
          <MyStats board={board} />
        ) : (
          <KindList board={board} kind={tab} me={me} />
        )}
        {board && (
          <p className="muted small">
            {tab === 'rate' || tab === 'mine' ? `${t('stats.rateNote', { n: board.minGames })} ` : ''}
            {t('stats.countNote')}
          </p>
        )}
      </div>
    </div>
  );
}

/** One filter: Any, or one of `options` (value, label). */
function FilterSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | undefined;
  options: readonly (readonly [T, string])[];
  onChange: (value: T | undefined) => void;
}) {
  return (
    <label className="stats-filter">
      <span className="muted small">{label}</span>
      <select value={value ?? ''} onChange={(e) => onChange((e.target.value || undefined) as T | undefined)}>
        <option value="">{t('stats.filter.any')}</option>
        {options.map(([v, name]) => (
          <option key={v} value={v}>
            {name}
          </option>
        ))}
      </select>
    </label>
  );
}

/** One tab's metrics: each a collapsible row with its leader, opening to the top ten and your line. */
function KindList({ board, kind, me }: { board: Board; kind: MetricKind; me: PlayerId | undefined }) {
  const metrics = board.metrics.filter((m) => DEFS.get(m.id)?.kind === kind);
  return (
    <div className="stats-list">
      {metrics.map((m) => {
        const leader = m.top[0];
        return (
          <details key={m.id} className="stats-metric">
            <summary title={t(`stats.metric.${m.id}.hint`)}>
              <span className="stats-name">{metricName(m.id)}</span>
              <span className="stats-leader">
                {leader ? (
                  <>
                    <span className="stats-who">{leader.name}</span> {formatMetric(m.id, leader.value)}
                  </>
                ) : (
                  <span className="muted">{t('stats.none')}</span>
                )}
              </span>
            </summary>
            <MetricTop board={m} me={me} />
          </details>
        );
      })}
    </div>
  );
}

function MetricTop({ board, me }: { board: MetricBoard; me: PlayerId | undefined }) {
  const { you } = board;
  const youLine =
    you.rank !== null && you.value !== null
      ? t('stats.you', { rank: t('stats.rank', { rank: you.rank }), value: formatMetric(board.id, you.value) })
      : you.value !== null
        ? t('stats.youUnranked', { value: formatMetric(board.id, you.value) })
        : t('stats.youNone');
  return (
    <div className="stats-top">
      <p className="muted small">{t(`stats.metric.${board.id}.hint`)}</p>
      {board.top.length === 0 ? (
        <p className="muted">{t('stats.empty')}</p>
      ) : (
        <ol>
          {board.top.map((e) => (
            <li key={e.playerId} className={e.playerId === me ? 'mine' : undefined}>
              <span className="stats-rank">{t('stats.rank', { rank: e.rank })}</span>
              <span className="stats-who">{e.name}</span>
              <span className="stats-value">{formatMetric(board.id, e.value)}</span>
            </li>
          ))}
        </ol>
      )}
      <p className="stats-you">{youLine}</p>
    </div>
  );
}

/** Your stat sheet: every metric by tab, your value and rank. */
function MyStats({ board }: { board: Board }) {
  const byId = new Map(board.metrics.map((m) => [m.id, m]));
  const cell = (def: MetricDef) => {
    const m = byId.get(def.id);
    const value = m?.you.value ?? null;
    return { value: value === null ? t('stats.none') : formatMetric(def.id, value), rank: m?.you.rank ?? null, m };
  };
  return (
    <div className="stats-mine">
      <p>{tn('stats.games', board.yourGames)}</p>
      {METRIC_KINDS.map((kind) => (
        <table key={kind} className="stats-table">
          <caption>{t(`stats.tab.${kind}`)}</caption>
          <thead>
            <tr>
              <th>{t('stats.col.stat')}</th>
              <th>{t('stats.col.you')}</th>
              <th>{t('stats.col.rank')}</th>
            </tr>
          </thead>
          <tbody>
            {METRICS.filter((d) => d.kind === kind).map((d) => {
              const c = cell(d);
              return (
                <tr key={d.id} title={t(`stats.metric.${d.id}.hint`)}>
                  <td>{metricName(d.id)}</td>
                  <td>{c.value}</td>
                  <td className={c.rank === 1 ? 'top' : undefined}>
                    {c.rank === null ? (
                      <span className="muted">{t('stats.notRanked')}</span>
                    ) : (
                      t('stats.rankOf', { rank: c.rank, n: c.m!.ranked })
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ))}
    </div>
  );
}
